import { useSelector } from '@xstate/react'
import { Array, Data, Equal, Match, Option, pipe } from 'effect'
import { Dollar } from 'libs/dollar'
import { formatCommas, formatK1OptionalDecimal } from 'libs/Number/formatNumber'
import { BULK_SKIPTRACE_RATE_LIMIT_NOTE, SHARED_SKIPTRACE_BUCKET_NOTE, SKIPTRACE_RATE_LIMIT_NOTE } from 'presentation/screens/Plans/components/SkiptraceDetailDisclosure/skiptraceRateLimitNotes'
import PlanComparisonMachine from 'presentation/screens/Plans/PlanComparisonV2/PlanComparisonMachine'
import ProductFeatureInterval from 'presentation/screens/Plans/PlanComparisonV2/domain/subscription/ProductFeatureInterval'
import { ProductFeatureSpec } from 'presentation/screens/Plans/PlanComparisonV2/domain/subscription/ProductFeatureSpec'

type ColumnValue = {
  readonly popoverValue: string
  readonly primaryText: string
  readonly secondaryText1: Option.Option<string>
  readonly secondaryText2: string
  readonly bucketNote: Option.Option<string>
  readonly rateLimitNote: Option.Option<string>
}

type RowValue = {
  readonly rowTitle: string
  readonly rowInfoText: Option.Option<string>
  readonly columns: readonly Option.Option<ColumnValue>[]
}

type SkiptraceFeatureRowViewModel = readonly RowValue[]

const isFeatureNamed = (featureName: 'Skiptrace' | 'BulkSkiptrace') =>
  (feature: ProductFeatureSpec): boolean =>
    feature.featureName === featureName

const hasFeatureNamed = (
  product: PlanComparisonMachine.Snapshot['context']['products'][number],
  featureName: 'Skiptrace' | 'BulkSkiptrace',
) => pipe(
  product.features,
  Array.some(isFeatureNamed(featureName)),
)

const toColumnValue = ({
  feature,
  hasSharedBucket,
  rateLimitNote,
}: {
  feature: ProductFeatureSpec
  hasSharedBucket: boolean
  rateLimitNote: Option.Option<string>
}): Option.Option<ColumnValue> => pipe(
  Match.value(feature),
  Match.tag('FeatureUnlimitedSpec', () => pipe(
    Data.struct<ColumnValue>({
      popoverValue: 'Unlimited',
      primaryText: 'Unlimited',
      secondaryText1: Option.none(),
      secondaryText2: '',
      bucketNote: hasSharedBucket ? Option.some(SHARED_SKIPTRACE_BUCKET_NOTE) : Option.none(),
      rateLimitNote,
    }),
    Option.some,
  )),
  Match.tag('FeatureQuotaSpec', f => pipe(
    Data.struct<ColumnValue>({
      popoverValue: formatCommas(f.quotaLimit),
      primaryText: `${formatK1OptionalDecimal(f.quotaLimit)} Free`,
      secondaryText1: pipe(
        Match.value(f.replenishInterval),
        Match.when('each', () => Option.some('per hit')),
        Match.orElse(() =>
          Option.some(`per ${ProductFeatureInterval.toSingularForm(f.replenishInterval)}`),
        ),
      ),
      secondaryText2: `${Dollar.formatPennies(f.overageCost)}/ea afterwards`,
      bucketNote: hasSharedBucket ? Option.some(SHARED_SKIPTRACE_BUCKET_NOTE) : Option.none(),
      rateLimitNote,
    }),
    Option.some,
  )),
  Match.orElse(() => Option.none()),
)

const presenter = (snapshot: PlanComparisonMachine.Snapshot) => {
  const visibleProducts = pipe(
    snapshot.context.products,
    Array.filter(product => pipe(
      product.plans,
      Array.some(plan =>
        plan._tag === (snapshot.context.isYearly ? 'YearlyPlan' : 'MonthlyPlan')),
    )),
  )

  const hasSplitSkiptraceFeatures = pipe(
    visibleProducts,
    Array.some(product =>
      hasFeatureNamed(product, 'BulkSkiptrace')),
  )

  const findColumns = (rowType: 'skiptrace' | 'bulk-skiptrace') => pipe(
    visibleProducts,
    Array.map(product => {
      const isLegacySharedBucket = hasSplitSkiptraceFeatures
        && !hasFeatureNamed(product, 'BulkSkiptrace')
        && hasFeatureNamed(product, 'Skiptrace')

      const featureName = !hasSplitSkiptraceFeatures
        ? 'Skiptrace'
        : rowType === 'skiptrace'
          ? 'Skiptrace'
          : hasFeatureNamed(product, 'BulkSkiptrace')
            ? 'BulkSkiptrace'
            : 'Skiptrace'

      if (!featureName) return Option.none<ColumnValue>()

      return pipe(
        product.features,
        Array.findFirst(isFeatureNamed(featureName)),
        Option.flatMap(feature => toColumnValue({
          feature,
          hasSharedBucket: isLegacySharedBucket,
          rateLimitNote: isLegacySharedBucket
            ? Option.none()
            : rowType === 'skiptrace'
              ? Option.some(SKIPTRACE_RATE_LIMIT_NOTE)
              : hasSplitSkiptraceFeatures
                ? Option.some(BULK_SKIPTRACE_RATE_LIMIT_NOTE)
                : Option.none(),
        })),
      )
    }),
    Data.array,
  )

  return pipe(
    hasSplitSkiptraceFeatures
      ? Array.make(
        Data.struct<RowValue>({
          rowTitle: 'Skiptrace',
          rowInfoText: Option.some(SKIPTRACE_RATE_LIMIT_NOTE),
          columns: findColumns('skiptrace'),
        }),
        Data.struct<RowValue>({
          rowTitle: 'Bulk Skiptrace',
          rowInfoText: Option.some(BULK_SKIPTRACE_RATE_LIMIT_NOTE),
          columns: findColumns('bulk-skiptrace'),
        }),
      )
      : Array.make(
        Data.struct<RowValue>({
          rowTitle: 'Skiptrace',
          rowInfoText: Option.none(),
          columns: findColumns('skiptrace'),
        }),
      ),
    Data.array,
  )
}

const usePresenter = () => {
  const actor = PlanComparisonMachine.useActorRefFromContext()
  return useSelector(
    actor,
    presenter,
    Equal.equals,
  )
}

const SkiptraceFeatureRowViewModel = {
  usePresenter,
}

export default SkiptraceFeatureRowViewModel
